import { apiClient } from './index';
import type { EntityType, Entity, TaskLink, EntityTask, WorkloadData, AssigneeInfo, HeatmapCell, AssignedTask } from './pm-types-ext';
export type { EntityType, Entity, TaskLink, EntityTask, WorkloadData, AssigneeInfo, HeatmapCell, AssignedTask };

// ─── Entity Types API ─────────────────────────────────────────────────────────

export async function getEntityTypes(): Promise<EntityType[]> {
    const { data } = await apiClient.get<{ data: EntityType[] }>('/entity-types');
    return data.data ?? [];
}

export async function createEntityType(input: Partial<EntityType>): Promise<EntityType> {
    const { data } = await apiClient.post<EntityType>('/entity-types', input);
    return data;
}

export async function getEntitiesByType(typeId: string): Promise<Entity[]> {
    const { data } = await apiClient.get<{ data: Entity[] }>(`/entity-types/${typeId}/entities`);
    return data.data ?? [];
}

// ─── Entities API ─────────────────────────────────────────────────────────────

export async function getAllEntities(): Promise<Entity[]> {
    const { data } = await apiClient.get<{ data: Entity[] }>('/entities');
    return data.data ?? [];
}

export async function getEntity(id: string): Promise<Entity> {
    const { data } = await apiClient.get<Entity>(`/entities/${id}`);
    return data;
}

export async function createEntity(input: Partial<Entity>): Promise<Entity> {
    const { data } = await apiClient.post<Entity>('/entities', input);
    return data;
}

export async function updateEntity(id: string, input: Partial<Entity>): Promise<Entity> {
    const { data } = await apiClient.put<Entity>(`/entities/${id}`, input);
    return data;
}

export async function deleteEntity(id: string): Promise<void> {
    await apiClient.delete(`/entities/${id}`);
}

export async function getChildEntities(parentId: string): Promise<Entity[]> {
    const { data } = await apiClient.get<{ data: Entity[] }>(`/entities/${parentId}/children`);
    return data.data ?? [];
}

/** Creates a task in the first available project then immediately links it to the entity. */
export async function createTaskForEntity(
    entityId: string,
    projectId: string,
    input: { title: string; description?: string; priority?: string; due_date?: string; assignee_id?: string }
): Promise<{ id: string; title: string; status: string; priority: string; due_date?: string; assignee_id?: string; created_at: string }> {
    const { data: task } = await apiClient.post<{ id: string; title: string; status: string; priority: string; due_date?: string; assignee_id?: string; created_at: string }>(
        `/projects/${projectId}/tasks`, { ...input, status: 'todo' }
    );
    await apiClient.post(`/tasks/${task.id}/links`, { entity_id: entityId });
    return task;
}

export async function getEntityTasks(entityId: string): Promise<EntityTask[]> {
    const { data } = await apiClient.get<{ data: EntityTask[] }>(`/entities/${entityId}/tasks`);
    return data.data ?? [];
}

// ─── Task Links API ───────────────────────────────────────────────────────────

export async function getTaskLinks(taskId: string): Promise<TaskLink[]> {
    const { data } = await apiClient.get<{ data: TaskLink[] }>(`/tasks/${taskId}/links`);
    return data.data ?? [];
}

export async function linkTaskToEntity(taskId: string, entityId: string): Promise<TaskLink> {
    const { data } = await apiClient.post<TaskLink>(`/tasks/${taskId}/links`, { entity_id: entityId });
    return data;
}

export async function unlinkTask(taskId: string, linkId: string): Promise<void> {
    await apiClient.delete(`/tasks/${taskId}/links/${linkId}`);
}

// ─── Workload API ─────────────────────────────────────────────────────────────

export async function getWorkloadHeatmap(from?: string, to?: string): Promise<WorkloadData> {
    const params: Record<string, string> = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const { data } = await apiClient.get<WorkloadData>('/workload/heatmap', { params });
    return data;
}
